import React, { useCallback, useMemo } from 'react';
import { useAction, useQuery } from 'convex/react';
import { api } from '../../convex/_generated/api';
import { useSceneSplitting, useSceneEditor } from '@/hooks/useSceneSplitting';
import { useSceneImageGeneration, useSingleSceneImage } from '@/hooks/useSceneImageGeneration';
import { useReelVoiceAssignment } from '@/hooks/useReelVoiceAssignment';
import { useVoiceoverGeneration } from '@/hooks/useVoiceoverGeneration';
import { useClonedVoices } from '@/hooks/useClonedVoices';
import { useAutoSceneSplit } from '@/hooks/useAutoSceneSplit';
import { useSelectedScene } from '@/hooks/useSelectedScene';
import { useReferencePersons } from '@/hooks/useReferencePersons';
import { useRenderScene } from '@/hooks/useRenderScene';
import { useSceneEditorHandlers } from '@/hooks/useSceneEditorHandlers';
import { useSceneEditorDerivedState } from '@/hooks/useSceneEditorDerivedState';
import { useReelVideoAdapter } from '@/adapters/reelVideoAdapter';
import { useGenerateAndAssembleVideo } from '@/hooks/useGenerateAndAssembleVideo';
import type { IReelScene, ISceneEditPayload, SceneSplitStatus } from '@/@types/reelScenes';
import type { FrameMode, AvatarSelection } from '@/@types/sceneEditorV2';
import type { VideoThumbnailSelection } from '@/@types/sceneEditorRedesign';
import type { Id } from '../../convex/_generated/dataModel';

interface IUseSceneEditorOrchestrationArgs {
  reelId: Id<'reels'>;
  sceneSplitStatus: SceneSplitStatus | undefined;
  frameMode: FrameMode;
  setFrameMode: React.Dispatch<React.SetStateAction<FrameMode>>;
  setIsTimelinePlaying: React.Dispatch<React.SetStateAction<boolean>>;
  setTimelineCurrentTime: React.Dispatch<React.SetStateAction<number>>;
  setShowFinalVideo: React.Dispatch<React.SetStateAction<boolean>>;
  selectedThumbnailOverride: VideoThumbnailSelection | null;
}

export function useSceneEditorOrchestration({
  reelId,
  sceneSplitStatus,
  frameMode,
  setFrameMode,
  setIsTimelinePlaying,
  setTimelineCurrentTime,
  setShowFinalVideo,
  selectedThumbnailOverride,
}: IUseSceneEditorOrchestrationArgs) {
  const { state: splittingState, splitReel } = useSceneSplitting(reelId);
  const { state: editorState, saveScene, deleteScene } = useSceneEditor(reelId);
  const { state: singleImageState, regenerateImage } = useSingleSceneImage();
  const { defaultVoiceId, setDefaultVoice } = useReelVoiceAssignment({ reelId });
  const {
    generateSingle,
    regenerate: regenerateVoiceover,
    voiceoversMap,
    generatingSceneIds: voiceoverGeneratingSceneIds,
  } = useVoiceoverGeneration({ reelId });
  const { activeVoices } = useClonedVoices();
  const { persons } = useReferencePersons();
  const libraryAvatars = useQuery(api.generatedAvatars.getReadyToUseAvatars) ?? [];

  const sortedScenes = useMemo(
    () =>
      [...(splittingState.scenes as IReelScene[])].sort((a, b) => a.sceneNumber - b.sceneNumber),
    [splittingState.scenes]
  );
  const { selectedIndex, selectedScene, selectScene } = useSelectedScene(sortedScenes);

  const firstFrameUrl =
    useQuery(
      api.storage.getUrl,
      selectedScene?.generatedImageId ? { storageId: selectedScene.generatedImageId } : 'skip'
    ) ?? null;
  const lastFrameUrl =
    useQuery(
      api.storage.getUrl,
      selectedScene?.lastFrameImageId ? { storageId: selectedScene.lastFrameImageId } : 'skip'
    ) ?? null;
  const avatarData = useQuery(
    api.generatedAvatars.getAvatarById,
    selectedScene?.libraryImageId ? { avatarId: selectedScene.libraryImageId } : 'skip'
  );
  const avatarImageUrl = avatarData?.imageUrl ?? null;
  const sceneVideo = useQuery(
    api.sceneVideos.getVideoByScene,
    selectedScene ? { sceneId: selectedScene._id } : 'skip'
  );
  // Captions in-flight: pending (scheduled) or processing (burning)
  const isBurningCaptions =
    sceneVideo?.captionedStatus === 'pending' || sceneVideo?.captionedStatus === 'processing';

  // 3-tier priority: captioned (with captions) > muxed (with audio) > raw
  // While captions are burning, still resolve to muxed/raw so the skeleton shows via isBurningCaptions flag
  const videoUrl =
    sceneVideo?.captionedVideoUrl && sceneVideo.captionedStatus === 'completed'
      ? sceneVideo.captionedVideoUrl
      : (sceneVideo?.muxedVideoUrl ?? sceneVideo?.videoUrl ?? null);

  const selectedThumbnail: VideoThumbnailSelection =
    selectedThumbnailOverride ?? (videoUrl ? 'video' : 'first-frame');

  const startSceneVideoGenerationAction = useAction(
    api.ai.videoGeneration.startSceneVideoGeneration
  );

  const { renderScene, isRendering } = useRenderScene({
    generateAudio: async (sceneId, voiceId) => {
      const voiceover = voiceoversMap?.get(sceneId);
      const scene = sortedScenes.find(s => s._id === sceneId);
      const effectiveText = scene ? (scene.editedVoiceoverText ?? scene.voiceoverText ?? '') : '';
      const isStale = voiceover?.audioUrl && voiceover.voiceoverText !== effectiveText;

      if (voiceover?.audioUrl && voiceover?.status === 'completed' && !isStale) return;

      if (scene) {
        if (isStale) {
          await regenerateVoiceover(sceneId, voiceId);
        } else {
          await generateSingle(sceneId, voiceId, effectiveText);
        }
      }
    },
    regenerateLastFrame: async sceneId => {
      await regenerateImage(sceneId, 'last');
    },
    generateVideo: async sceneId => {
      await startSceneVideoGenerationAction({ sceneId });
    },
  });

  const {
    scenesWithoutImages,
    totalDuration,
    effectiveValues,
    isGeneratingSelectedSceneImages,
    isGeneratingSelectedSceneVideo,
    canRenderSelectedScene,
    avatarName,
    voiceName,
    sceneThumbnailUrls,
    scriptPanelVoiceoversMap,
  } = useSceneEditorDerivedState({
    scenes: splittingState.scenes as IReelScene[],
    selectedScene,
    defaultVoiceId,
    activeVoices,
    persons,
    voiceoversMap,
    isRendering,
    avatarData: avatarData ?? null,
  });

  const { generateAllImages, isGenerating: isGeneratingImages } = useSceneImageGeneration(
    reelId,
    scenesWithoutImages
  );

  const {
    adapter,
    isReady: isAllScenesReady,
    muxedVideos,
  } = useReelVideoAdapter(reelId, sortedScenes.length);
  const startAllVideosAction = useAction(api.ai.videoGeneration.startAllSceneVideoGeneration);
  const retryFailedAction = useAction(api.ai.videoGeneration.retryFailedSceneVideos);

  // Track all scene videos to detect failed ones
  const allSceneVideos = useQuery(api.sceneVideos.getVideosByReel, { reelId });
  const failedVideoCount = useMemo(
    () => allSceneVideos?.filter(v => v.status === 'failed' || v.status === 'pending').length ?? 0,
    [allSceneVideos]
  );

  const readyCount = useMemo(
    () =>
      muxedVideos?.filter(v => {
        // A scene is ready if it has either a completed muxed video or a direct video URL
        if (v.muxedVideoUrl !== null && v.muxedStatus === 'completed') return true;
        return v.videoUrl !== null;
      }).length ?? 0,
    [muxedVideos]
  );

  const {
    generate: generateFullReel,
    isGenerating: isGeneratingFull,
    statusLabel: generateStatusLabel,
    overallProgress: generateProgress,
    phase: generatePhase,
  } = useGenerateAndAssembleVideo({
    adapter,
    isAllScenesReady,
    scenesReadyCount: readyCount,
    scenesTotalCount: sortedScenes.length,
    failedVideoCount,
    onGeneratePrerequisites: scenesWithoutImages > 0 ? () => generateAllImages() : undefined,
    onGenerateAllVideos: async () => {
      // If there are failed videos, use retry action instead
      if (failedVideoCount > 0) {
        const result = await retryFailedAction({ reelId });
        return { started: result.retryResult.started, total: result.retryResult.total };
      }
      const result = await startAllVideosAction({ reelId });
      return { started: result.started, total: result.total };
    },
    onComplete: () => setShowFinalVideo(true),
  });

  const existingReelVideo = useQuery(api.reelVideos.getReelVideo, { reelId });
  const hasFinalVideo = existingReelVideo != null && existingReelVideo.videoUrl != null;
  // Check if the final video is stale (scene videos changed since it was created)
  const isFinalVideoStale = useMemo(() => {
    if (!hasFinalVideo || !existingReelVideo || !muxedVideos) return false;
    const currentVideoIds = muxedVideos.map(v => v._id).sort();
    const reelVideoIds = [...existingReelVideo.sceneVideoIds].sort();
    if (currentVideoIds.length !== reelVideoIds.length) return true;
    return currentVideoIds.some((id, i) => id !== reelVideoIds[i]);
  }, [hasFinalVideo, existingReelVideo, muxedVideos]);
  const effectiveGenerateLabel =
    generatePhase === 'idle' && hasFinalVideo && !isFinalVideoStale ? 'Done!' : generateStatusLabel;
  const handlePostSplitDefaults = useCallback(async () => {
    const scenes = splittingState.scenes as IReelScene[];
    if (scenes.length === 0) return;
    const defaultPerson = persons.find(p => p.isDefault);
    if (defaultPerson && scenes.length >= 2) {
      const first = scenes[0];
      const last = scenes[scenes.length - 1];
      if (first) await saveScene(first._id, { editedSceneType: 'avatar_only' });
      if (last && last._id !== first?._id)
        await saveScene(last._id, { editedSceneType: 'avatar_only' });
    }
    const defaultVoice = activeVoices[0];
    if (defaultVoice) await setDefaultVoice(defaultVoice._id);
    await generateAllImages();
  }, [splittingState.scenes, persons, activeVoices, saveScene, setDefaultVoice, generateAllImages]);
  const { isAutoSplitting, noScriptError } = useAutoSceneSplit({
    reelId,
    hasScript: true,
    scenesCount: splittingState.scenes.length,
    sceneSplitStatus,
    isSplitting: splittingState.isSplitting,
    splitReel,
    onSplitComplete: handlePostSplitDefaults,
  });
  const {
    handleUpdateVoiceoverText,
    handleDeleteScene,
    handlePlayAudio,
    handleRegenerateAudio,
    handleRenderScene,
    handleRegenerateImage,
    handleToggleFrameMode,
    handleAddScene,
    handleTimelinePlayPause,
    handleTimelineSeek,
    audioPlayingSceneId,
  } = useSceneEditorHandlers({
    saveScene,
    deleteScene,
    voiceoversMap: voiceoversMap ?? new Map(),
    defaultVoiceId,
    generateSingle,
    regenerateVoiceover,
    sortedScenes,
    selectedScene,
    regenerateImage,
    renderScene,
    frameMode,
    setFrameMode,
    setIsTimelinePlaying,
    setTimelineCurrentTime,
  });

  const handleSelectAvatar = useCallback(
    (selection: AvatarSelection) => {
      let payload: ISceneEditPayload;
      switch (selection.kind) {
        case 'none':
          payload = { referencePersonId: undefined, libraryImageId: null };
          break;
        case 'reference-person':
          payload = { referencePersonId: selection.personId, libraryImageId: null };
          break;
        case 'library-avatar':
          payload = { referencePersonId: undefined, libraryImageId: selection.avatarId };
          break;
      }
      const avatarScenes = sortedScenes.filter(s => {
        const type = s.editedSceneType ?? s.sceneType;
        return (
          type === 'avatar_only' || type === 'avatar_broll_split' || type === 'avatar_image_split'
        );
      });
      for (const scene of avatarScenes) {
        void saveScene(scene._id, payload);
      }
    },
    [sortedScenes, saveScene]
  );

  return {
    splittingState,
    editorState,
    singleImageState,
    sortedScenes,
    selectedIndex,
    selectedScene,
    selectScene,
    saveScene,
    firstFrameUrl,
    lastFrameUrl,
    avatarImageUrl,
    videoUrl,
    selectedThumbnail,
    isRendering,
    isGeneratingImages,
    isGeneratingFull,
    totalDuration,
    effectiveValues,
    isGeneratingSelectedSceneImages,
    isGeneratingSelectedSceneVideo,
    canRenderSelectedScene,
    avatarName,
    voiceName,
    sceneThumbnailUrls,
    scriptPanelVoiceoversMap,
    voiceoverGeneratingSceneIds,
    effectiveGenerateLabel,
    generateProgress,
    generateFullReel,
    isAutoSplitting,
    noScriptError,
    handleUpdateVoiceoverText,
    handleDeleteScene,
    handlePlayAudio,
    handleRegenerateAudio,
    handleRenderScene,
    handleRegenerateImage,
    handleToggleFrameMode,
    handleAddScene,
    handleTimelinePlayPause,
    handleTimelineSeek,
    audioPlayingSceneId,
    handleSelectAvatar,
    setDefaultVoice,
    persons,
    activeVoices,
    libraryAvatars,
    isBurningCaptions,
  };
}
